import { createContext, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

const CartContext = createContext();

const fetchCart = () => {
  const storedCart = localStorage.getItem("cart");
  return storedCart ? JSON.parse(storedCart) : [];
};

const CartProvider = ({ children }) => {
  const queryClient = useQueryClient();

  const { data: cart = [], refetch } = useQuery({
    queryKey: ["cart"],
    queryFn: fetchCart,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const updateCart = useCallback(
    (updatedCart) => {
      localStorage.setItem("cart", JSON.stringify(updatedCart));
      queryClient.setQueryData(["cart"], updatedCart);
    },
    [queryClient]
  );

  const addToCart = useCallback(
    (product) => {
      const currentCart = queryClient.getQueryData(["cart"]) || [];
      const existing = currentCart.find(
        (item) =>
          item.product_id === product.product_id &&
          item.variant_id === product.variant_id
      );

      if (existing) {
        const updatedCart = currentCart.map((item) =>
          item.product_id === product.product_id &&
            item.variant_id === product.variant_id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
        updateCart(updatedCart);
        toast.success("Quantity updated");
      } else {
        const newItem = { ...product, quantity: 1 };
        updateCart([...currentCart, newItem]);
        toast.success("Product added to cart");
      }
    },
    [queryClient, updateCart]
  );

  const removeFromCart = useCallback(
    (itemIdentifier) => {
      const currentCart = queryClient.getQueryData(["cart"]) || [];
      const updatedCart = currentCart.filter(
        (item) => (item.cartItemId || item.product_id) !== itemIdentifier
      );
      updateCart(updatedCart);
      toast.success("Product removed");
    },
    [queryClient, updateCart]
  );

  const increment = useCallback(
    (itemIdentifier) => {
      const currentCart = queryClient.getQueryData(["cart"]) || [];
      const item = currentCart.find((i) => (i.cartItemId || i.product_id) === itemIdentifier);
      if (item && item.quantity >= 15) {
        toast.error("Maximum Order Quantity 15");
        return;
      }

      const updatedCart = currentCart.map((item) =>
        (item.cartItemId || item.product_id) === itemIdentifier
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
      updateCart(updatedCart);
    },
    [queryClient, updateCart]
  );

  const decrement = useCallback(
    (itemIdentifier) => {
      const currentCart = queryClient.getQueryData(["cart"]) || [];
      const item = currentCart.find((i) => (i.cartItemId || i.product_id) === itemIdentifier);
      if (item && item.quantity <= 1) {
        toast.error("Minimum order quantity is 1");
        return;
      }

      const updatedCart = currentCart.map((item) =>
        (item.cartItemId || item.product_id) === itemIdentifier
          ? { ...item, quantity: item.quantity - 1 }
          : item
      );
      updateCart(updatedCart);
    },
    [queryClient, updateCart]
  );

  const clearCart = useCallback(() => {
    localStorage.removeItem("cart");
    queryClient.setQueryData(["cart"], []);
  }, [queryClient]);

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        increment,
        decrement,
        clearCart,
        refetch,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export { CartContext, CartProvider };
